// src/shared/email-sort.js — how a store sorts its mail (v129.44).
//
// Dubai Hills, 4 Aug: "quick way of sorting mails (inbox and sent items)
// whether by sender, date, subject … easier to backread emails and sent items."
//
// Pure and zero-dependency so the rule is testable without a browser, in the
// same spirit as src/shared/holiday-weekly.js. The list is already in memory —
// the inbox loads a page of 50 and appends older pages on demand — so sorting
// costs no API call and no new endpoint.
//
// ── THE TRAP THIS MODULE IS SHAPED AROUND ──────────────────────────────────
//
// Sorting only ever sorts what is LOADED. Someone who sorts Subject A→Z on a
// mailbox with older pages unfetched is looking at the A-Z of the most recent
// 50, not of the mailbox — and nothing on screen would tell them. That is the
// same class of quiet wrongness as an empty section that means "the read
// failed": it looks like an answer. So `sortNote()` exists, the UI shows it
// whenever more pages remain, and the honest fix is one tap ("Load older").
//
// Date-descending is the DEFAULT and stays byte-identical to what the inbox
// did before this file existed: a mail app that reorders itself on you is a
// mail app you stop trusting.

export const SORT_KEYS = ['date', 'who', 'subject'];

// The label for the who-column depends on the folder, because the row itself
// already says different things: Inbox shows the sender, Sent shows recipients.
// Calling both "From" would be a lie in one of the two.
export function whoLabel(folder) {
  return folder === 'out' ? 'To' : 'From';
}

// What the row is sorted BY for the 'who' key — the same value the row DISPLAYS.
// Sorting an inbox by a field the reader cannot see is indistinguishable from
// sorting it randomly.
export function whoValue(m, folder) {
  if (!m) return '';
  if (folder === 'out' || m.direction === 'out') {
    return (Array.isArray(m.to) ? m.to.join(', ') : String(m.to || '')).trim();
  }
  return String(m.from || '').trim();
}

// Gmail-ish: strip a leading Re:/Fwd:/Fw: chain so a reply files under the
// conversation's own letter rather than under R. Repeats ("Re: Fwd: Re: x").
const REPLY_PREFIX = /^\s*(re|fw|fwd|aw|sv|vs)\s*(\[\d+\])?\s*:\s*/i;
export function subjectKey(raw) {
  let s = String(raw == null ? '' : raw).trim();
  for (let i = 0; i < 5 && REPLY_PREFIX.test(s); i++) s = s.replace(REPLY_PREFIX, '').trim();
  return s.toLowerCase();
}

// Sort a loaded list. Never mutates the input — _emails is the cache every
// other view reads, and reordering it in place would silently reorder them too.
//
// `dir` is 'asc' | 'desc'. Rows with no value for the chosen key sink to the
// BOTTOM in both directions: a block of "(no subject)" at the top of an A-Z is
// noise, and flipping the arrow should not promote it.
export function sortEmails(list, opts) {
  const o = opts || {};
  const key = SORT_KEYS.indexOf(o.key) !== -1 ? o.key : 'date';
  const dir = o.dir === 'asc' ? 'asc' : 'desc';
  const folder = o.folder === 'out' ? 'out' : 'in';
  const sign = dir === 'asc' ? 1 : -1;

  const valueOf = (m) => {
    if (key === 'date') return String((m && m.received_at) || '');
    if (key === 'who') return whoValue(m, folder).toLowerCase();
    return subjectKey(m && m.subject);
  };

  return (Array.isArray(list) ? list.slice() : []).sort((a, b) => {
    const va = valueOf(a);
    const vb = valueOf(b);
    if (!va && !vb) return 0;
    if (!va) return 1;          // empties last, whichever way the arrow points
    if (!vb) return -1;
    if (va === vb) {
      // Stable-looking tiebreak: newest first, so an A-Z by sender still reads
      // chronologically within one person's mail.
      return String((b && b.received_at) || '').localeCompare(String((a && a.received_at) || ''));
    }
    return va < vb ? -sign : sign;
  });
}

// The caveat line, or null when there is nothing to caveat. `hasMore` is the
// inbox's own "a full page came back, so older mail exists" flag.
export function sortNote(key, hasMore) {
  if (!hasMore) return null;
  if (key === 'date') return null;   // newest-first on a paged-newest list is honest
  return 'Sorting the emails loaded so far — tap "Load older emails" at the bottom to include the rest.';
}
